import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  AlertCircle,
  Menu,
  X,
} from 'lucide-react';
import Sidebar from './components/Sidebar';
import AdminNavTabs from './components/AdminNavTabs';
import ContentEditor from './components/ContentEditor';
import StickySaveBar from './components/StickySaveBar';
import './NewAdminPanel.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const NewAdminPanel = () => {
  const [token] = useState(localStorage.getItem('admin_token'));
  const [selectedSection, setSelectedSection] = useState(
    () => localStorage.getItem('admin_selected_section') || null,
  );
  const [changes, setChanges] = useState({}); // { [componentId]: { 'path.to.field': value } }
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [saveError, setSaveError] = useState(null);
  const [pendingSection, setPendingSection] = useState(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  const queryClient = useQueryClient();

  const hasUnsavedChanges = Object.keys(changes).length > 0;

  // Сохраняем выбранную секцию между перезагрузками
  useEffect(() => {
    if (selectedSection) {
      localStorage.setItem('admin_selected_section', selectedSection);
    } else {
      localStorage.removeItem('admin_selected_section');
    }
  }, [selectedSection]);

  // Предупреждение при закрытии вкладки
  useEffect(() => {
    const handler = (e) => {
      if (!hasUnsavedChanges) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // Проверка аутентификации
  useEffect(() => {
    if (!token) {
      window.location.href = '/admin/login';
    }
  }, [token]);

  // Загрузка данных
  const { data, isLoading, error } = useQuery({
    queryKey: ['adminPages', token],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/admin/pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data; // { pages: [...] }
    },
    enabled: !!token,
    staleTime: 1000 * 60 * 5, // 5 минут
  });

  // Загрузка языков
  const { data: languagesData } = useQuery({
    queryKey: ['adminLanguages', token],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/admin/languages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data; // { languages: [...] }
    },
    enabled: !!token,
  });

  const languages = languagesData?.languages || [];

  // Применение локальных изменений к контенту компонента
  const applyChangesToContent = (originalContent, componentId) => {
    const compChanges = changes[componentId];
    if (!compChanges) {
      return typeof originalContent === 'string'
        ? JSON.parse(originalContent)
        : originalContent || {};
    }

    const base =
      typeof originalContent === 'string'
        ? JSON.parse(originalContent)
        : JSON.parse(JSON.stringify(originalContent || {}));

    Object.entries(compChanges).forEach(([pathStr, value]) => {
      if (pathStr === '__mediaId__') return;
      const path = pathStr.split('.');
      let current = base;
      for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        const nextKey = path[i + 1];
        if (current[key] === undefined || current[key] === null) {
          current[key] = /^\d+$/.test(String(nextKey)) ? [] : {};
        }
        current = current[key];
      }
      current[path[path.length - 1]] = value;
    });

    return base;
  };

  // Сохранение всей страницы (все языки, все компоненты)
  const savePageMutation = useMutation({
    mutationFn: async ({ systemKey, payload }) => {
      const response = await axios.put(
        `${API_URL}/admin/pages/${systemKey}/full`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    },
    onMutate: () => setSaveError(null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminPages'] });
      setChanges({});
      setSaveError(null);
    },
    onError: (err) => {
      const data = err?.response?.data;
      const msg =
        data?.error ||
        (data?.details ? `Проверьте данные: ${JSON.stringify(data.details)}` : null) ||
        err?.message ||
        'Не удалось сохранить страницу';
      setSaveError(msg);
    },
  });

  // Группировка страниц для боковой панели
  const sections = useMemo(() => {
    if (!data?.pages) return {};
    const grouped = {};
    data.pages.forEach((page) => {
      const title =
        page.title && typeof page.title === 'object'
          ? page.title.en || page.title.ru || page.title.uz || page.systemKey
          : page.title || page.systemKey;

      grouped[page.systemKey] = {
        name: title,
        order: page.id,
        items: page.components || [],
      };
    });
    return grouped;
  }, [data]);

  // Выбранная страница
  const selectedPage = useMemo(() => {
    if (!selectedSection || !data?.pages) return null;
    return data.pages.find((p) => p.systemKey === selectedSection);
  }, [selectedSection, data]);

  // Обновление локального значения
  // path - массив ключей, например ['hero', 'title', 'en']
  const updateLocalValue = (componentId, path, value) => {
    setChanges((prev) => ({
      ...prev,
      [componentId]: {
        ...(prev[componentId] || {}),
        [path.join('.')]: value,
      },
    }));
  };

  const handleSavePage = () => {
    if (!selectedPage) return;

    const componentsPayload = (selectedPage.components || []).map((comp) => {
      const mergedContent = applyChangesToContent(comp.content, comp.id);
      const compChanges = changes[comp.id] || {};
      const mediaId = Object.prototype.hasOwnProperty.call(
        compChanges,
        '__mediaId__',
      )
        ? compChanges.__mediaId__
        : comp.mediaId ?? null;

      return {
        id: comp.id,
        pageId: comp.pageId,
        type: comp.type,
        order: comp.order,
        mediaId,
        content: mergedContent,
      };
    });

    const payload = {
      title: selectedPage.title,
      slug: selectedPage.slug,
      seo: selectedPage.seo,
      isPublished: selectedPage.isPublished ?? true,
      components: componentsPayload,
    };

    savePageMutation.mutate({
      systemKey: selectedPage.systemKey,
      payload,
    });
  };

  if (isLoading) {
    return (
      <div className="admin-panel-loading">
        <div className="spinner"></div>
        <p>Загрузка данных...</p>
      </div>
    );
  }

  if (error) {
    const errorStatus = error?.response?.status;
    const errorMessage =
      error?.response?.data?.error ||
      (errorStatus === 429
        ? 'Слишком много запросов к серверу. Подождите 1–2 минуты и обновите страницу.'
        : null) ||
      error?.message;

    // Если 403 или 401 - перенаправляем на логин
    if (errorStatus === 403 || errorStatus === 401) {
      localStorage.removeItem('admin_token');
      setTimeout(() => {
        window.location.href = '/admin/login';
      }, 1000);
      return (
        <div className="admin-panel-error">
          <AlertCircle size={48} />
          <h2>Сессия истекла</h2>
          <p>Перенаправление на страницу входа...</p>
        </div>
      );
    }

    return (
      <div className="admin-panel-error">
        <AlertCircle size={48} />
        <h2>Ошибка загрузки данных</h2>
        <p>{errorMessage || error.message}</p>
        {error?.message === 'Network Error' && (
          <p className="admin-panel-error-hint">
            Нет ответа от API (порт 5000): сервер мог перезагрузиться после нагрузки или
            сработал лимит запросов. Подождите минуту и нажмите «Попробовать снова».
          </p>
        )}
        <button onClick={() => queryClient.invalidateQueries({ queryKey: ['adminPages'] })}>
          Попробовать снова
        </button>
      </div>
    );
  }

  const requestSectionChange = (nextSection) => {
    if (nextSection === selectedSection) return;
    if (hasUnsavedChanges) {
      setPendingSection(nextSection);
      setShowLeaveConfirm(true);
    } else {
      setSelectedSection(nextSection);
    }
  };

  return (
    <div className="new-admin-panel">
      {/* Header */}
      <header className="admin-header">
        <div className="header-left">
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <h1>CMS Admin Panel</h1>
          <AdminNavTabs />
        </div>
        <div className="header-right">
          <button
            className="logout-btn"
            onClick={() => {
              localStorage.removeItem('admin_token');
              window.location.href = '/admin/login';
            }}
          >
            Выйти
          </button>
        </div>
      </header>

      <div className="admin-layout">
        {/* Sidebar */}
        <Sidebar
          sections={sections}
          selectedSection={selectedSection}
          onSelectSection={requestSectionChange}
          isOpen={sidebarOpen}
        />

        {/* Main Content */}
        <main className="admin-main">
          <div className="admin-main-content">
            {selectedPage ? (
              <ContentEditor
                components={selectedPage.components || []}
                languages={languages}
                onUpdate={updateLocalValue}
                systemKey={selectedPage.systemKey}
                changes={changes}
                token={token}
              />
            ) : (
              <div className="admin-panel-empty">
                <p>Выберите страницу слева для редактирования контента.</p>
              </div>
            )}
          </div>
        </main>
      </div>

      {hasUnsavedChanges && (
        <StickySaveBar
          onSave={handleSavePage}
          onCancel={() => {
            setChanges({});
            setSaveError(null);
          }}
          isSaving={savePageMutation.isPending}
          error={saveError}
        />
      )}
      {showLeaveConfirm && (
        <div className="leave-modal-backdrop">
          <div className="leave-modal">
            <h3>Есть несохранённые изменения</h3>
            <p>Сохранить или выйти без сохранения?</p>
            <div className="leave-modal-actions">
              <button
                type="button"
                className="leave-btn-secondary"
                onClick={() => {
                  setShowLeaveConfirm(false);
                  setPendingSection(null);
                }}
              >
                Отмена
              </button>
              <button
                type="button"
                className="leave-btn-danger"
                onClick={() => {
                  setChanges({});
                  setSaveError(null);
                  setSelectedSection(pendingSection);
                  setPendingSection(null);
                  setShowLeaveConfirm(false);
                }}
              >
                Выйти без сохранения
              </button>
              <button
                type="button"
                className="leave-btn-primary"
                onClick={() => {
                  setShowLeaveConfirm(false);
                  setPendingSection(null);
                  handleSavePage();
                }}
              >
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewAdminPanel;
